import type { SceneAuxiliaryObjectHost } from '../../types';

/**
 * 场景辅助显示根的统一身份约定。
 *
 * 与可拾取性、持久化策略分离：操作手柄可以命中，但不能充当组件变换根。
 * 所有者在挂载前声明，消费者沿父链继承；WeakSet 不拥有对象，无需清理 GPU 资源，
 * 对象销毁后不会被注册表保活。声明不进入 JSON，也不修改 raycast 或 children。
 */
export class SceneAuxiliaryObjectRegistry {
  /** 实例生命周期内固定的辅助身份；只能在首次挂载前声明。 */
  private static readonly roots = new WeakSet<object>();
  /** 集中识别已有领域标记；新领域使用显式声明，不继续扩散 userData 协议。 */
  private static readonly legacyFlags = [
    'isImportedModelOverlay',
    'isImportedModelOverlayChannel',
    'isGlbDatumSlotOverlay',
    'isDatumPlaneLabel',
    'isAssemblyHoverBoundingBox',
    'isImportedModelTreeHoverBoundingBox',
    'isMeasureAnchorMarker',
  ] as const;

  /**
   * 声明整个子树只承载辅助显示或工具交互，必须在 add/attach 前调用。
   * @param object 辅助根；不得声明包含真实业务组件的混合容器。
   */
  public static markRoot(object: SceneAuxiliaryObjectHost): void {
    SceneAuxiliaryObjectRegistry.roots.add(object);
  }

  /**
   * 识别当前节点自身的辅助身份；不沿父链遍历，也不分配临时集合。
   * @param object 当前节点。
   * @returns 显式辅助根、既有辅助标记或 Three.js 网格/坐标轴工具返回 true。
   */
  public static isRoot(object: SceneAuxiliaryObjectHost): boolean {
    if (SceneAuxiliaryObjectRegistry.roots.has(object)) return true;
    if (object.type === 'GridHelper' || object.type === 'AxesHelper') return true;
    const data = object.userData;
    if (!data || typeof data !== 'object') return false;
    // skipSelection 只表示不可选，绝不能因此排除普通业务根或原始渲染 Mesh。
    const flags = data as Record<string, unknown>;
    for (const key of SceneAuxiliaryObjectRegistry.legacyFlags) {
      if (flags[key] === true) return true;
    }
    return false;
  }
}
